/**
 * The shared 5e vocabulary (P0-4b deliverable 1). Keys are dnd5e 5.x's short
 * keys, so a Foundry import and export map one to one; the US English labels
 * keep the 2014 rules' printed spelling.
 *
 * Every schema here follows the rules of the brief: Zod 4, non-recursive,
 * strict objects, every property present (null for absent), no records,
 * patterns checked with `.refine`, and array minimums of 0 or 1.
 */
import { z } from './zod.js';
// ---------------------------------------------------------------- abilities
export const ABILITIES = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
export const Ability = z.enum(ABILITIES);
export const ABILITY_LABELS = {
    str: 'Strength',
    dex: 'Dexterity',
    con: 'Constitution',
    int: 'Intelligence',
    wis: 'Wisdom',
    cha: 'Charisma',
};
// ------------------------------------------------------------------- skills
/** The 18 skills, with the ability each uses by default. */
export const SKILL_ABILITIES = {
    acr: 'dex',
    ani: 'wis',
    arc: 'int',
    ath: 'str',
    dec: 'cha',
    his: 'int',
    ins: 'wis',
    itm: 'cha',
    inv: 'int',
    med: 'wis',
    nat: 'int',
    prc: 'wis',
    prf: 'cha',
    per: 'cha',
    rel: 'int',
    slt: 'dex',
    ste: 'dex',
    sur: 'wis',
};
export const SKILLS = Object.keys(SKILL_ABILITIES);
export const Skill = z.enum(SKILLS);
export const SKILL_LABELS = {
    acr: 'Acrobatics',
    ani: 'Animal Handling',
    arc: 'Arcana',
    ath: 'Athletics',
    dec: 'Deception',
    his: 'History',
    ins: 'Insight',
    itm: 'Intimidation',
    inv: 'Investigation',
    med: 'Medicine',
    nat: 'Nature',
    prc: 'Perception',
    prf: 'Performance',
    per: 'Persuasion',
    rel: 'Religion',
    slt: 'Sleight of Hand',
    ste: 'Stealth',
    sur: 'Survival',
};
// -------------------------------------------------------------------- sizes
export const SIZES = ['tiny', 'sm', 'med', 'lg', 'huge', 'grg'];
export const Size = z.enum(SIZES);
export const SIZE_LABELS = {
    tiny: 'Tiny',
    sm: 'Small',
    med: 'Medium',
    lg: 'Large',
    huge: 'Huge',
    grg: 'Gargantuan',
};
// ------------------------------------------------------------- damage types
export const DAMAGE_TYPES = [
    'acid',
    'bludgeoning',
    'cold',
    'fire',
    'force',
    'lightning',
    'necrotic',
    'piercing',
    'poison',
    'psychic',
    'radiant',
    'slashing',
    'thunder',
];
export const DamageType = z.enum(DAMAGE_TYPES);
export const DAMAGE_TYPE_LABELS = {
    acid: 'Acid',
    bludgeoning: 'Bludgeoning',
    cold: 'Cold',
    fire: 'Fire',
    force: 'Force',
    lightning: 'Lightning',
    necrotic: 'Necrotic',
    piercing: 'Piercing',
    poison: 'Poison',
    psychic: 'Psychic',
    radiant: 'Radiant',
    slashing: 'Slashing',
    thunder: 'Thunder',
};
/** What lets an attack past a damage trait, as dnd5e's `bypasses`: magical, silvered, adamantine. */
export const DAMAGE_BYPASSES = ['mgc', 'sil', 'ada'];
export const DamageBypass = z.enum(DAMAGE_BYPASSES);
export const DAMAGE_BYPASS_LABELS = {
    mgc: 'Magical',
    sil: 'Silvered',
    ada: 'Adamantine',
};
// --------------------------------------------------------------- conditions
/** The 15 conditions of the 2014 rules (exhaustion included). */
export const CONDITIONS = [
    'blinded',
    'charmed',
    'deafened',
    'exhaustion',
    'frightened',
    'grappled',
    'incapacitated',
    'invisible',
    'paralyzed',
    'petrified',
    'poisoned',
    'prone',
    'restrained',
    'stunned',
    'unconscious',
];
export const Condition = z.enum(CONDITIONS);
export const CONDITION_LABELS = {
    blinded: 'Blinded',
    charmed: 'Charmed',
    deafened: 'Deafened',
    exhaustion: 'Exhaustion',
    frightened: 'Frightened',
    grappled: 'Grappled',
    incapacitated: 'Incapacitated',
    invisible: 'Invisible',
    paralyzed: 'Paralyzed',
    petrified: 'Petrified',
    poisoned: 'Poisoned',
    prone: 'Prone',
    restrained: 'Restrained',
    stunned: 'Stunned',
    unconscious: 'Unconscious',
};
// -------------------------------------------------------- weapon properties
/** dnd5e 5.x's weapon property keys that the 2014 rules and the DMG use. */
export const WEAPON_PROPERTIES = [
    'ada',
    'amm',
    'fin',
    'fir',
    'foc',
    'hvy',
    'lgt',
    'lod',
    'mgc',
    'rch',
    'rel',
    'ret',
    'sil',
    'spc',
    'thr',
    'two',
    'ver',
];
export const WeaponProperty = z.enum(WEAPON_PROPERTIES);
export const WEAPON_PROPERTY_LABELS = {
    ada: 'Adamantine',
    amm: 'Ammunition',
    fin: 'Finesse',
    fir: 'Firearm',
    foc: 'Focus',
    hvy: 'Heavy',
    lgt: 'Light',
    lod: 'Loading',
    mgc: 'Magical',
    rch: 'Reach',
    rel: 'Reload',
    ret: 'Returning',
    sil: 'Silvered',
    spc: 'Special',
    thr: 'Thrown',
    two: 'Two-Handed',
    ver: 'Versatile',
};
// ------------------------------------------------------------ spell schools
export const SPELL_SCHOOLS = ['abj', 'con', 'div', 'enc', 'evo', 'ill', 'nec', 'trs'];
export const SpellSchool = z.enum(SPELL_SCHOOLS);
export const SPELL_SCHOOL_LABELS = {
    abj: 'Abjuration',
    con: 'Conjuration',
    div: 'Divination',
    enc: 'Enchantment',
    evo: 'Evocation',
    ill: 'Illusion',
    nec: 'Necromancy',
    trs: 'Transmutation',
};
// ----------------------------------------------------------- creature types
export const CREATURE_TYPES = [
    'aberration',
    'beast',
    'celestial',
    'construct',
    'dragon',
    'elemental',
    'fey',
    'fiend',
    'giant',
    'humanoid',
    'monstrosity',
    'ooze',
    'plant',
    'undead',
];
export const CreatureType = z.enum(CREATURE_TYPES);
export const CREATURE_TYPE_LABELS = {
    aberration: 'Aberration',
    beast: 'Beast',
    celestial: 'Celestial',
    construct: 'Construct',
    dragon: 'Dragon',
    elemental: 'Elemental',
    fey: 'Fey',
    fiend: 'Fiend',
    giant: 'Giant',
    humanoid: 'Humanoid',
    monstrosity: 'Monstrosity',
    ooze: 'Ooze',
    plant: 'Plant',
    undead: 'Undead',
};
// ---------------------------------------------------------------- rarities
export const RARITIES = [
    'common',
    'uncommon',
    'rare',
    'veryRare',
    'legendary',
    'artifact',
];
export const Rarity = z.enum(RARITIES);
export const RARITY_LABELS = {
    common: 'Common',
    uncommon: 'Uncommon',
    rare: 'Rare',
    veryRare: 'Very Rare',
    legendary: 'Legendary',
    artifact: 'Artifact',
};
// -------------------------------------------------------------------- dice
export const DICE = [4, 6, 8, 10, 12, 20, 100];
export const Die = z.literal(DICE);
/** `2d6 + 3` is `{count: 2, die: 6, bonus: 3}`; a single die has count 1; no bonus is 0. */
export const Dice = z.strictObject({
    count: z.number().int().min(0),
    die: Die,
    bonus: z.number().int(),
});
/**
 * One part of a damage line, as printed: `7 (2d6) fire damage` is
 * `{average: 7, dice: 2d6, flat: null, type: 'fire'}`; `1 piercing damage`,
 * with no dice, is `{average: null, dice: null, flat: 1, type: 'piercing'}`.
 * `note` keeps a condition, such as "if used with two hands".
 */
export const DamagePart = z.strictObject({
    average: z.number().int().min(0).nullable(),
    dice: Dice.nullable(),
    flat: z.number().int().nullable(),
    type: DamageType.nullable(),
    note: z.string().nullable(),
});
// --------------------------------------------------------- challenge rating
export const CHALLENGE_RATINGS = [
    0, 0.125, 0.25, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
    22, 23, 24, 25, 26, 27, 28, 29, 30,
];
/** A number: 1/8 is 0.125, never the string "1/8". */
export const ChallengeRating = z.literal(CHALLENGE_RATINGS);
// -------------------------------------------------------------------- money
/** The standard coins and their worth in gp; any other key is kept but has no known rate. */
export const CURRENCY_RATES = {
    pp: 10,
    gp: 1,
    ep: 0.5,
    sp: 0.1,
    cp: 0.01,
};
export const MoneyEntry = z.strictObject({
    key: z.string().min(1),
    amount: z.number().min(0),
});
/** Every currency key is kept, including one with no known rate. */
export const Money = z.array(MoneyEntry).refine((list) => allDistinct(list.map((m) => m.key)), {
    message: 'Each currency key appears once',
});
/** The worth in gp of the known coins; an unknown key is left out, never guessed. */
export function goldValue(money) {
    let total = 0;
    for (const { key, amount } of money) {
        const rate = CURRENCY_RATES[key];
        if (rate !== undefined)
            total += amount * rate;
    }
    return Math.round(total * 100) / 100;
}
// ---------------------------------------------------------------------- ids
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isUuid(value) {
    return UUID.test(value);
}
/** Every feature, action, trait, spell entry and item carries its own id (`actor_sheet.data`). */
export const FeatureId = z
    .string()
    .refine(isUuid, { message: 'Expected a uuid' })
    .meta({ format: 'uuid', description: 'The local id of this feature' });
const FOUNDRY_ID = /^[A-Za-z0-9]{16}$/;
export function isFoundryId(value) {
    return FOUNDRY_ID.test(value);
}
/** A Foundry document id (`fvid`): 16 letters or digits. */
export const FoundryId = z
    .string()
    .refine(isFoundryId, { message: 'Expected a 16-character Foundry id' })
    .describe('A Foundry document id: 16 letters or digits');
// ----------------------------------------------------------------- source
export const SOURCE_KINDS = ['form', 'paste', 'srd', 'srd_copy', 'ai', 'foundry'];
export const FOUNDRY_LAYOUTS = ['5.0-5.2', '5.3'];
/** Where a sheet or card came from. Derived numbers are never authoritative: `deriveStats` recomputes them. */
export const SourceMeta = z.strictObject({
    kind: z.enum(SOURCE_KINDS),
    upstreamKey: z.string().min(1).nullable(),
    foundryLayout: z.enum(FOUNDRY_LAYOUTS).nullable(),
});
/** The source kinds a spell or item card may have: `rules_record.source` has no `foundry`. */
export const CARD_SOURCE_KINDS = ['form', 'paste', 'srd', 'srd_copy', 'ai'];
/** Where a spell or item card came from (`rules_record.source`). */
export const CardSourceMeta = SourceMeta.extend({ kind: z.enum(CARD_SOURCE_KINDS) });
/** `source.rules` of a Foundry actor or item. The app's own rules version is 2014 only. */
export const RulesVersion = z.enum(['2014', '2024']);
// ---------------------------------------------------------------- helpers
/** A text that must say something. */
export const Text = z.string().min(1);
/** Whether no two values are the same (a refinement for lists keyed by a field). */
export function allDistinct(values) {
    return new Set(values).size === values.length;
}
/** A damage vulnerability, resistance or immunity line, split into its printed groups. */
export const DamageTraitGroup = z.strictObject({
    types: z.array(DamageType),
    bypasses: z.array(DamageBypass),
    /** The qualifier as printed, such as "from nonmagical attacks that aren't silvered". */
    qualifier: z.string().nullable(),
});
/** An armor class entry: `15 (natural armor)` or `18 (plate)`. */
export const AcEntry = z.strictObject({
    value: z.number().int().min(0),
    source: z.string().nullable(),
});
//# sourceMappingURL=common.js.map